'use client';

import { useState } from 'react';

import {
  Box,
  Stack,
  Button,
  Divider,
  Popover,
  MenuItem,
  TextField,
  Typography,
  ButtonBase,
} from '@mui/material';

import { campaignPostGroupNameError, CAMPAIGN_POST_GROUP_NAME_MAX } from 'src/utils/campaign/campaign-post-groups';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { CampaignPostGroupChip } from './campaign-post-group-chip';

type CampaignPostGroupPickerProps = {
  /** 지금 그룹 — 없으면 `null` */
  group: string | null;
  /** 고를 칸(이 캠페인에서 쓴 이름) — 비면 새 이름 칸만 선다 */
  options: readonly string[];
  /** 그룹 정하기 — `null` 이면 빼기 */
  onChange: (group: string | null) => void;
  /** 그룹이 없을 때 칩 글 */
  emptyLabel?: string;
  disabled?: boolean;
};

/**
 * 시딩 캠페인 글 그룹 고르개 — 칩을 누르면 고를 칸 · 새 그룹 이름 넣기 · 「그룹 빼기」 판이 열린다.
 * 상세 포스팅 표의 「그룹」 칸과 캠페인 창의 링크 칸이 같이 쓴다. 그룹이 있으면 색 칩(`CampaignPostGroupChip`), 없으면 점선 글자.
 * 쓸 수 없는 이름(`_none` · `__…__`)은 칸 아래에 「쓸 수 없는 이름이에요」를 띄우고 「추가」를 잠근다.
 * 판 안 상태(열림 · 새 이름)는 이 칩만 쓴다.
 */
export function CampaignPostGroupPicker({ group, options, onChange, emptyLabel = '+ 그룹', disabled = false }: CampaignPostGroupPickerProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState('');

  const close = () => {
    setAnchor(null);
    setDraft('');
  };
  const pick = (next: string | null) => {
    close();
    if (next !== group) onChange(next);
  };
  const draftName = draft.trim();
  const draftError = draftName ? campaignPostGroupNameError(draftName) : null;
  const canAdd = !!draftName && !draftError;

  return (
    <>
      <ButtonBase
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={group ? `그룹 ${group} 바꾸기` : '그룹 정하기'}
        sx={group ? { borderRadius: RADIUS.sm } : {
          px: 1,
          py: 0.5,
          borderRadius: RADIUS.sm,
          border: '1px dashed',
          borderColor: 'divider',
          color: 'text.disabled',
          typography: 'uiMeta',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {group ? <CampaignPostGroupChip name={group} /> : emptyLabel}
      </ButtonBase>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 240, mt: 0.5, p: 1, borderRadius: RADIUS.md } } }}
      >
        {options.map((name) => (
          <MenuItem key={name} selected={name === group} onClick={() => pick(name)} sx={{ typography: 'uiBody', minHeight: 32, px: 1, borderRadius: RADIUS.sm }}>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}><CampaignPostGroupChip name={name} /></Box>
            {name === group && <Iconify icon="eva:checkmark-fill" width={16} sx={{ color: 'primary.main' }} />}
          </MenuItem>
        ))}
        {options.length > 0 && <Divider sx={{ my: 1 }} />}
        <Typography component="p" variant="uiMeta" color="text.secondary" sx={{ px: 0.5, mb: 0.5 }}>
          새 그룹 만들기
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing && canAdd) pick(draftName);
            }}
            placeholder="그룹 이름"
            error={!!draftError}
            inputProps={{ maxLength: CAMPAIGN_POST_GROUP_NAME_MAX, 'aria-label': '새 그룹 이름' }}
            sx={{
              '& .MuiInputBase-root': { height: 32, borderRadius: RADIUS.sm, typography: 'uiBody' },
              '& .MuiInputBase-input': { typography: 'uiBody', py: 0, px: 1.5 },
            }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!canAdd}
            onClick={() => pick(draftName)}
            sx={{ height: 32, minWidth: 52, flexShrink: 0, borderRadius: RADIUS.sm, typography: 'uiAction' }}
          >
            추가
          </Button>
        </Stack>
        {draftError && (
          <Typography component="p" variant="uiMeta" color="error.main" sx={{ px: 0.5, mt: 0.5 }}>
            {draftError}
          </Typography>
        )}
        {group && (
          <MenuItem onClick={() => pick(null)} sx={{ typography: 'uiBody', color: 'error.main', minHeight: 32, px: 1, mt: 1, borderRadius: RADIUS.sm }}>
            <Typography component="span" variant="inherit">그룹 빼기</Typography>
          </MenuItem>
        )}
      </Popover>
    </>
  );
}
